/**
 * Pure region-grow helpers — no Cornerstone or module-state dependency.
 * Extracted from RegionGrowTool so they can be reused by nnInteractiveSession
 * (FWHM-adaptive bbox) without creating an import cycle.
 */

type Pt2 = [number, number];

// ─── Contrast-aware FWHM constants (#167) ────────────────────────────────────

export const DEFAULT_FWHM_FLOOR = 130;
/**
 * Higher stone floor on contrast-enhanced series. Calcium / urate stones do
 * NOT take up contrast — the stone's own HU stays ~200–1000+ on every phase.
 * The shift is in the BACKGROUND: kidney parenchyma jumps from ~30 HU
 * (non-contrast) into the 100–300 HU band in arterial/portal/excretory
 * phases, overlapping with stone HU. The previous floor (80) lived inside
 * that enhanced-parenchyma band and flood-fill leaked the whole kidney.
 *
 * Setting the floor to 200 keeps the fill above plausible enhanced cortex/
 * medulla on most contrast phases while still admitting real stones (which
 * are calcified and brighter). Tradeoff: soft (<200 HU) uric-acid stones
 * may be missed on contrast — the user can fall back to lasso or nnInteractive
 * if that comes up clinically.
 */
export const CONTRAST_FWHM_FLOOR = 200;
export const DEFAULT_HU_TOLERANCE = 50;
/**
 * Keep the upper-bound tolerance tight on contrast too. The previous widened
 * value (80) accepted bright excretory-phase contrast in the renal pelvis as
 * "still part of the stone," another leakage source.
 */
export const CONTRAST_HU_TOLERANCE = 50;

// ─── Flood-fill (2D, 8-connected, HU-bounded, radius-clipped) ────────────────

export function floodFill2D(
  scalarData: any,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
  minHU: number,
  maxHU: number,
  maxRadiusPx: number,
): Uint8Array | null {
  const mask = new Uint8Array(cols * rows);
  const seedIdx = seedRow * cols + seedCol;
  const seedHU = scalarData[seedIdx] as number;
  if (seedHU < minHU || seedHU > maxHU) {
    console.warn(
      `[RegionGrowTool] Seed HU=${seedHU.toFixed(0)} outside fill range [${minHU}, ${maxHU}] — skipping`,
    );
    return null;
  }

  const queue: Pt2[] = [[seedRow, seedCol]];
  mask[seedIdx] = 1;
  let count = 1;
  const maxRadSq = maxRadiusPx * maxRadiusPx;
  // 8-connected: cardinal + diagonal neighbors for smoother stone boundaries
  const dr = [-1, -1, -1, 0, 0, 1, 1, 1];
  const dc = [-1, 0, 1, -1, 1, -1, 0, 1];
  let head = 0;

  while (head < queue.length) {
    const [r, c] = queue[head++];
    for (let i = 0; i < 8; i++) {
      const nr = r + dr[i],
        nc = c + dc[i];
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      const nIdx = nr * cols + nc;
      if (mask[nIdx]) continue;
      if ((nr - seedRow) ** 2 + (nc - seedCol) ** 2 > maxRadSq) continue;
      const hu = scalarData[nIdx] as number;
      if (hu < minHU || hu > maxHU) continue;
      mask[nIdx] = 1;
      count++;
      queue.push([nr, nc]);
    }
  }

  return count >= 2 ? mask : null;
}

// ─── Adaptive HU threshold (FWHM-style) ─────────────────────────────────────

/**
 * Mean HU in a ring 15–20 px around (seedRow, seedCol). Returns null when no
 * pixels in the ring are inside the image (seed at corner with small image).
 * Pulled out of adaptiveMinHU so the click handler can reuse the same
 * background sample for the *initial* FWHM threshold, not just the retry.
 */
export function sampleBackgroundMeanHU(
  scalarData: any,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
): number | null {
  let bgSum = 0,
    bgCount = 0;
  for (let dr = -20; dr <= 20; dr++) {
    for (let dc = -20; dc <= 20; dc++) {
      const distSq = dr * dr + dc * dc;
      if (distSq < 15 * 15 || distSq > 20 * 20) continue;
      const nr = seedRow + dr,
        nc = seedCol + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      bgSum += scalarData[nr * cols + nc] as number;
      bgCount++;
    }
  }
  if (bgCount === 0) return null;
  return bgSum / bgCount;
}

/**
 * Compute an adaptive minimum HU for region grow based on FWHM analysis.
 *
 * For small high-contrast structures (stones), the edges blend with
 * surrounding tissue due to partial volume effects. A fixed threshold
 * misses these edges. Instead:
 *   1. Sample background HU in a ring around the seed (15-20px radius)
 *   2. Set the threshold at the midpoint between background and seed HU
 *     (Demehri et al., 2011 — true FWHM).
 *
 * This captures the full visible extent of the structure while excluding
 * surrounding tissue that lives below the midpoint (e.g. contrast-enhanced
 * renal cortex when the stone is brighter than the cortex).
 */
export function adaptiveMinHU(
  scalarData: any,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
  seedHU: number,
): number {
  const bgMean = sampleBackgroundMeanHU(scalarData, cols, rows, seedRow, seedCol);
  if (bgMean === null) return 0;
  // Threshold halfway between background and seed HU
  const adaptiveThresh = bgMean + (seedHU - bgMean) * 0.5;
  return Math.round(adaptiveThresh);
}

// ─── Brightest-pixel snap (argmax in a small box) ────────────────────────────

/**
 * Snap (seedRow, seedCol) to the highest-HU pixel within `radius` pixels.
 * Plain argmax — no valley-awareness, suitable for FWHM bbox computation
 * where a tiny offset doesn't matter and we want a deterministic seed.
 */
export function snapToBrightestArgmax(
  scalarData: any,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
  radius: number,
): { row: number; col: number; hu: number } {
  let bestRow = seedRow,
    bestCol = seedCol;
  let bestHU = scalarData[seedRow * cols + seedCol] as number;
  for (let dr = -radius; dr <= radius; dr++) {
    for (let dc = -radius; dc <= radius; dc++) {
      const nr = seedRow + dr,
        nc = seedCol + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (dr * dr + dc * dc > radius * radius) continue;
      const hu = scalarData[nr * cols + nc] as number;
      if (hu > bestHU) {
        bestHU = hu;
        bestRow = nr;
        bestCol = nc;
      }
    }
  }
  return { row: bestRow, col: bestCol, hu: bestHU };
}
